import type { Ref } from "vue";
import type { ChangeAccountInfoModel, ResetPasswordModel, SignUpModel } from "./utils";
import type { SsoResponse } from "@/generated/models";
import type { AxShareHostsConfig } from "@/services/models/config";
import { createSharedComposable } from "@vueuse/core";
import { computed, readonly, ref } from "vue";
import { isString } from "@/common/general";
import { useDiskoServerAuthentication } from "../disko/useDiskoServerAuthentication";
import { useAxureCloudAccountService } from "./useAxureCloudAccountService";
import { useDesktopAppAuthentication } from "./useDesktopAppAuthentication";

function useAccountServiceFn() {
  const desktopAppAuthentication = useDesktopAppAuthentication();
  const axureCloudAccountService = useAxureCloudAccountService();
  const diskoServerAuthentication = useDiskoServerAuthentication();

  const user = ref<SsoResponse & { isVisitor?: true }>();
  const error: Ref<Error | undefined> = ref(undefined);
  const isLoading = ref(false);

  function setError(fetchError: unknown) {
    if (!fetchError) return;

    if (isString(fetchError)) {
      error.value = new Error(fetchError);
    } else if (fetchError instanceof Error) {
      error.value = fetchError;
    } else {
      error.value = new Error(fetchError as unknown as string);
    }
    return error.value;
  }

  function clearAuth() {
    user.value = undefined;
  }

  const isAuthenticated = computed(() => !!user.value?.success);

  async function login(email: string, password: string, redirect?: string) {
    try {
      await axureCloudAccountService.login(email, password, redirect);
    } catch (err) {
      setError(err);
      throw err;
    }
  }

  async function checkSession(withConfig?: AxShareHostsConfig) {
    try {
      isLoading.value = true;

      const { appServerUrl, accountServerUrl } = axureCloudAccountService;
      if (!appServerUrl.value || !accountServerUrl.value) {
        throw new Error("Axure Cloud account service is not configured");
      }

      user.value = await axureCloudAccountService.auth(withConfig);
      await diskoServerAuthentication.loginToken({
        authToken: user.value.authToken,
        axureCloudUrl: appServerUrl.value,
        accountServiceUrl: accountServerUrl.value,
      });
      await desktopAppAuthentication.setAuth(user.value.authToken);

      return isAuthenticated.value;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function authenticateAnonymously() {
    try {
      isLoading.value = true;

      const { appServerUrl, accountServerUrl } = axureCloudAccountService;
      if (!appServerUrl.value || !accountServerUrl.value) {
        throw new Error("Axure Cloud account service is not configured");
      }

      await diskoServerAuthentication.loginAnonymous({
        axureCloudUrl: appServerUrl.value,
        accountServiceUrl: accountServerUrl.value,
      });

      return isAuthenticated.value;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    try {
      try {
        await diskoServerAuthentication.logout();
      } catch {
        // ignore disko logout error
      }
      await axureCloudAccountService.logout();
      await desktopAppAuthentication.logout();
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      clearAuth();
    }
  }

  async function updateUserProfileName(nickname: string) {
    if (user.value?.nickname === nickname) return;
    const response = await axureCloudAccountService.updateUserProfileName(nickname);
    if (response.success && user.value) user.value.nickname = nickname;
  }

  async function uploadUserProfileImg(file: File) {
    const response = await axureCloudAccountService.uploadUserProfileImg(file);
    if (response.success && response.imgUrl && user.value) user.value.profileImageUrl = response.imgUrl;
  }

  async function deleteUserProfileImg() {
    const response = await axureCloudAccountService.deleteUserProfileImg();
    if (response.success && user.value) user.value.profileImageUrl = "";
  }

  async function changeAccountInfo(model: ChangeAccountInfoModel) {
    const response = await axureCloudAccountService.changeAccountInfo(model);
    if (response.success && user.value && model.newEmail) user.value.userEmail = model.newEmail;
  }

  async function forgotPassword(email: string, target: string, redirect?: string) {
    try {
      return await axureCloudAccountService.forgotPassword(email, target, redirect);
    } catch (err) {
      setError(err);
      throw err;
    }
  }

  async function signUp(options: SignUpModel) {
    try {
      await axureCloudAccountService.signUp(options);
    } catch (err) {
      setError(err);
      throw err;
    }
  }

  async function resetPasswordValidateToken(token: string) {
    return await axureCloudAccountService.resetPasswordValidateToken(token);
  }

  async function resetPassword(options: ResetPasswordModel) {
    return await axureCloudAccountService.resetPassword(options);
  }

  async function visitorLogin(email: string) {
    try {
      user.value = await axureCloudAccountService.visitorLogin(email);
      user.value.isVisitor = true;
    } catch (err) {
      setError(err);
      throw err;
    }
  }

  return {
    user: readonly(user),
    isAuthenticated,
    isLoading: readonly(isLoading),
    error: readonly(error),
    checkSession,
    authenticateAnonymously,
    login,
    visitorLogin,
    logout,
    updateUserProfileName,
    uploadUserProfileImg,
    deleteUserProfileImg,
    changeAccountInfo,
    forgotPassword,
    getTimeZones: axureCloudAccountService.getTimeZones,
    setUserTimeZone: axureCloudAccountService.setUserTimeZone,
    getLoginFullUrl: axureCloudAccountService.getLoginFullUrl,
    getPrototypeLoginPopupUrl: axureCloudAccountService.getPrototypeLoginPopupUrl,
    getSignupUrl: axureCloudAccountService.getSignupUrl,
    getExternalLoginUrl: axureCloudAccountService.getExternalLoginUrl,
    getJoinAxureUrl: axureCloudAccountService.getJoinAxureUrl,
    isSameAsApiHost: axureCloudAccountService.isSameAsApiHost,
    signUp,
    resetPasswordValidateToken,
    resetPassword,
  };
}

export const useAccountService = createSharedComposable(useAccountServiceFn);
